import ComponentPlaceholder from 'components/ComponentPlaceholder';
import Img from 'components/Img';
import { LayoutContainer } from 'components/Layout';
import RichText from 'components/RichText';
import Text from 'components/Text';
import type { DigizuiteAsset } from 'models/asset';
import type { ColorField, Field, SitecoreBlockProps } from 'models/sitecore';
import type { HTMLString } from 'types';
import { getEditorMargin } from 'utils/business-logic';
import { filterTruthy } from 'utils/collection';
import { getAsset } from 'utils/helpers';

interface Item {
	content: Field<HTMLString>;
	heading: Field<string>;
	squareImage: Field<DigizuiteAsset[]>;
}

interface Props extends SitecoreBlockProps {
	fields?: {
		backgroundColor?: ColorField;
		items: Item[];
	};
}

export default function USP({ fields, params }: Props) {
	if (!fields) {
		return null;
	}
	const backgroundColor = fields.backgroundColor?.fields?.colorHex?.value;

	const usps = filterTruthy(
		fields.items?.map((item) => ({
			content: item?.content?.value,
			heading: item?.heading?.value,
			image: getAsset('Source Copy', item?.squareImage),
		})),
		'content',
		'heading',
		'image',
	);

	if (usps.length === 0) {
		return (
			<ComponentPlaceholder
				className={getEditorMargin(params)}
				componentName="Usp"
				description="Requires at least one usp item with heading, content and square image"
			/>
		);
	}

	return (
		<LayoutContainer
			outerClassName={getEditorMargin(params)}
			innerClassName="grid grid-cols-4 md:grid-cols-12 md:gap-4 lg:gap-6"
		>
			{usps.map((usp) => (
				<div
					key={`${usp.image.src}-${usp.heading}`}
					className="col-span-full flex items-start gap-4 p-4 first:rounded-t-md last:rounded-b-md md:col-span-6 md:flex-col md:items-center md:rounded-md md:p-8 md:forced-colors-outline lg:col-span-4 lg:gap-6 lg:px-12 lg:py-20"
					style={backgroundColor ? { backgroundColor } : undefined}
				>
					<Img
						height={224}
						width={224}
						className="w-16 shrink-0 md:w-56"
						src={usp.image.src}
						alt={usp.image.alt}
					/>
					<div className="min-w-0">
						<Text as="h2" styleAs="h3" text={usp.heading} />
						<RichText className="mt-r-sm" html={usp.content} />
					</div>
				</div>
			))}
		</LayoutContainer>
	);
}
USP.displayName = 'USP';
